import { ApolloClient, InMemoryCache, createHttpLink, from, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from "@sentry/react";
import { getAccessToken, getRefreshToken, clearTokens, saveTokens } from './token-utils';
import { REFRESH_AUTH_TOKEN } from '@/graphql/mutations';
import { authErrorEmitter, categorizeAuthError } from '@/contexts/AuthErrorContext';
import type { GraphQLError } from 'graphql';

// Get GraphQL endpoint from environment variable
// Must be set via VITE_FLOW_GRAPHQL_ENDPOINT environment variable
const GRAPHQL_ENDPOINT = import.meta.env.VITE_FLOW_GRAPHQL_ENDPOINT;

if (!GRAPHQL_ENDPOINT) {
  throw new Error('VITE_FLOW_GRAPHQL_ENDPOINT environment variable is required');
}

// HTTP Link for real backend
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Function to refresh the access token
async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn('[Token Refresh] No refresh token available');
    return false;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('[Token Refresh] Attempting to refresh access token...');
      
      // Create a temporary client without auth to call refresh mutation
      const refreshClient = new ApolloClient({
        link: createHttpLink({ uri: GRAPHQL_ENDPOINT }),
        cache: new InMemoryCache(),
      });

      const result = await refreshClient.mutate<{
        refreshAuthToken: {
          success: boolean;
          tokens?: {
            accessToken: string;
            refreshToken: string;
            expiresIn?: number;
          };
          error?: string;
        };
      }>({
        mutation: REFRESH_AUTH_TOKEN,
        variables: { refreshToken },
      });

      const refreshResult = result.data?.refreshAuthToken;
      
      if (refreshResult?.success && refreshResult?.tokens) {
        console.log('[Token Refresh] Token refresh successful');
        saveTokens(
          refreshResult.tokens.accessToken,
          refreshResult.tokens.refreshToken
        );
        return true;
      } else {
        console.warn('[Token Refresh] Token refresh failed:', refreshResult?.error);
        return false;
      }
    } catch (error) {
      console.error('[Token Refresh] Error refreshing token:', error);
      Sentry.captureException(error, {
        tags: { area: "auth", action: "refresh_access_token" },
      });
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Auth Link - adds authentication token to headers
const authLink = setContext((_, { headers }) => {
  const token = getAccessToken();
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error Link - handles GraphQL errors and attempts token refresh
const errorLink = onError((options) => {
  const { operation, forward } = options;
  const { graphQLErrors, networkError } = options as typeof options & {
    graphQLErrors?: readonly GraphQLError[];
    networkError?: Error | null;
  };
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      const { message, locations, path } = error as GraphQLError;
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );

      Sentry.addBreadcrumb({
        category: "graphql",
        level: "error",
        message: "GraphQL error",
        data: {
          operationName: operation?.operationName,
          message,
          path,
        },
      });
      
      // First, check if this is a categorizable auth error (tenant access, user not found, etc.)
      const categorizedError = categorizeAuthError(message, path as readonly string[] | undefined);
      
      // Check for various authentication/authorization errors
      const isAuthError = 
        message === 'Unauthorized' || 
        message === 'authentication required' || 
        message.includes('authentication') ||
        message.includes('user does not have access to this tenant') ||
        message.includes('tenant access') ||
        message.includes('token expired') ||
        message.includes('invalid token') ||
        message.includes('user not found');

      // For tenant access and user not found errors, emit to the UI instead of auto-redirecting
      // These errors typically can't be fixed by token refresh
      const isNonRefreshableError = 
        message.includes('user does not have access to this tenant') ||
        message.includes('user not found');

      if (isNonRefreshableError && categorizedError) {
        console.warn('[Apollo Error Link] Non-refreshable auth error detected, emitting to UI:', categorizedError.type);
        Sentry.addBreadcrumb({
          category: "auth",
          level: "warning",
          message: "Non-refreshable auth error",
          data: { type: categorizedError.type, operationName: operation?.operationName },
        });
        // Emit the error to the UI to show the auth error screen
        // Small delay to ensure React context is mounted
        setTimeout(() => {
          authErrorEmitter.emit(categorizedError);
        }, 0);
        // Don't redirect or try to refresh - let the UI handle it
        return;
      }

      if (isAuthError) {
        // Check if we have a refresh token and can attempt refresh
        const refreshToken = getRefreshToken();
        
        if (refreshToken && operation && forward) {
          // Attempt to refresh the token and retry the operation
          return new Observable((observer) => {
            let subscription: { unsubscribe: () => void } | null = null;

            refreshAccessToken()
              .then((success) => {
                if (success) {
                  console.log('[Apollo Error Link] Token refreshed, retrying operation');
                  // Retry the failed operation with the new token
                  subscription = forward(operation).subscribe({
                    next: (value) => observer.next(value),
                    error: (err) => observer.error(err),
                    complete: () => observer.complete(),
                  });
                } else {
                  console.warn('[Apollo Error Link] Token refresh failed');
                  Sentry.captureMessage("Token refresh failed", {
                    level: "warning",
                    tags: { area: "auth", action: "refresh_access_token" },
                    extra: { operationName: operation?.operationName },
                  });
                  // If we have a categorized error, emit it to the UI
                  if (categorizedError) {
                    authErrorEmitter.emit(categorizedError);
                  } else {
                    // Fallback: redirect to login
                    clearTokens();
                    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                      window.location.href = '/login';
                    }
                  }
                  observer.error(new Error('Token refresh failed'));
                }
              })
              .catch((error) => {
                console.error('[Apollo Error Link] Error during token refresh:', error);
                Sentry.captureException(error, {
                  tags: { area: "auth", action: "refresh_access_token" },
                  extra: { operationName: operation?.operationName },
                });
                // If we have a categorized error, emit it to the UI
                if (categorizedError) {
                  authErrorEmitter.emit(categorizedError);
                } else {
                  clearTokens();
                  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                    window.location.href = '/login';
                  }
                }
                observer.error(error);
              });
            
            // Cleanup function
            return () => {
              if (subscription) {
                subscription.unsubscribe();
              }
            };
          });
        } else {
          // No refresh token available
          console.warn('[Apollo Error Link] No refresh token available');
          Sentry.addBreadcrumb({
            category: "auth",
            level: "warning",
            message: "No refresh token available",
            data: { operationName: operation?.operationName },
          });
          // If we have a categorized error, emit it to the UI
          if (categorizedError) {
            setTimeout(() => {
              authErrorEmitter.emit(categorizedError);
            }, 0);
          } else {
            // Fallback: redirect to login
            clearTokens();
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
      }
    }
  }

  if (networkError) {
    // Check if this is a 422 validation error (not a true network error)
    const networkErrorExt = networkError as Error & { statusCode?: number; response?: { status?: number }; result?: { statusCode?: number } };
    const statusCode = networkErrorExt?.statusCode ||
                      networkErrorExt?.response?.status ||
                      networkErrorExt?.result?.statusCode;
    
    const errorMessage = networkError.message || String(networkError);
    const is422Error = statusCode === 422 || errorMessage.includes('status code 422');
    
    if (is422Error) {
      console.error(`[Validation error]: Server returned 422 (Unprocessable Entity)`);
      console.error(`[GraphQL endpoint]: ${GRAPHQL_ENDPOINT}`);
      console.error(`[Operation]: ${operation?.operationName || 'unknown'}`);
      console.error(`[Error details]:`, networkError);
      return;
    }
    
    // Log actual network errors
    console.error(`[Network error]: ${networkError}`);
    console.error(`[GraphQL endpoint]: ${GRAPHQL_ENDPOINT}`);

    Sentry.captureException(networkError, {
      tags: { area: "graphql", kind: "network_error" },
      extra: {
        operationName: operation?.operationName,
        graphQLEndpoint: GRAPHQL_ENDPOINT,
        statusCode,
      },
    });
    
    if (networkError.message?.includes('Failed to fetch') || networkError.message?.includes('NetworkError')) {
      console.error(
        `\n⚠️  GraphQL endpoint is not reachable!\n` +
        `   Endpoint: ${GRAPHQL_ENDPOINT}\n` +
        `   Please ensure:\n` +
        `   1. Your GraphQL backend is running\n` +
        `   2. The endpoint URL is correct\n` +
        `   3. CORS is properly configured on your backend\n`
      );
    }
  }
});

// Create Apollo Client
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          sessions: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default client;
